import { useState } from 'react';
import { AppBar, Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SHELL_LAYOUT_SIZES } from '@mightyhive/theme';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  CalculateOutlined,
  GridViewOutlined,
  HomeOutlined,
  LayersOutlined,
  PeopleAltOutlined,
  WorkOutline,
} from '@mui/icons-material';
import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from '@mightyhive/analytics';
import { ShellSidebar } from '@mightyhive/commoncomponents';
import { useUser } from '../context/UserContext';
import { RouteLocations } from '../routes/RouteLocations';
import { HypeChatBox } from '../components/chat/HypeChatBox';

interface NavItem {
  label: string;
  Icon: SvgIconComponent;
  path: string;
  internalOnly?: boolean;
}

const baseNavItems: NavItem[] = [
  { label: 'Home', Icon: HomeOutlined, path: RouteLocations.HOME },
  { label: 'Clients', Icon: PeopleAltOutlined, path: RouteLocations.CLIENTS, internalOnly: true },
  { label: 'Library', Icon: WorkOutline, path: RouteLocations.LIBRARY },
  { label: 'New Experiment', Icon: GridViewOutlined, path: `${RouteLocations.GRID}/create-experiment` },
  { label: 'Data Sources', Icon: LayersOutlined, path: RouteLocations.DATA_SOURCES },
  { label: 'Analysis', Icon: CalculateOutlined, path: RouteLocations.ANALYSIS },
];

const ROUTE_PAGE_TITLES: Record<string, string> = {
  [RouteLocations.HOME]: 'Home',
  [RouteLocations.LIBRARY]: 'Library',
  [RouteLocations.DATA_SOURCES]: 'Data Sources',
  [RouteLocations.CLIENTS]: 'Clients',
};

function resolvePageTitle(pathname: string): string {
  if (pathname.startsWith(RouteLocations.LIBRARY)) return ROUTE_PAGE_TITLES[RouteLocations.LIBRARY];
  if (pathname.startsWith(RouteLocations.GRID)) return 'Experimentation Engine';
  if (pathname.startsWith(RouteLocations.ANALYSIS)) return 'Experimentation Engine';

  const exactTitle = ROUTE_PAGE_TITLES[pathname];
  return exactTitle ?? 'Experimentation Engine';
}

function isNavItemActive(pathname: string, path: string): boolean {
  if (path.startsWith(RouteLocations.GRID)) return pathname.startsWith(RouteLocations.GRID);
  if (path === RouteLocations.ANALYSIS) return pathname.startsWith(RouteLocations.ANALYSIS);
  if (path === RouteLocations.LIBRARY) return pathname.startsWith(RouteLocations.LIBRARY);
  return pathname === path;
}

export function Layout(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const {
    avatarUrl,
    email,
    name,
    isInternalUser,
    isPoweruser,
    clients,
    selectedClient,
    selectedClientExternalUuid,
    setSelectedClientExternalUuid,
  } = user;
  const { identify } = useAnalytics();
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('isSidebarHidden') === 'false');
  const navItems = baseNavItems.filter((item) => !item.internalOnly || isInternalUser);

  const pageTitle = resolvePageTitle(location.pathname);
  const drawerWidth = sidebarOpen
    ? SHELL_LAYOUT_SIZES.sidebarWidthExpanded
    : SHELL_LAYOUT_SIZES.sidebarWidthCollapsed;

  const isGridRoute = location.pathname.startsWith(RouteLocations.GRID);
  const isHomeRoute = location.pathname === RouteLocations.HOME;
  const isLibraryRoute = location.pathname.startsWith(RouteLocations.LIBRARY);
  const isClientsRoute = location.pathname === RouteLocations.CLIENTS;
  const isAnalysisRoute = location.pathname.startsWith(RouteLocations.ANALYSIS);
  const isFullBleedRoute = isGridRoute || isClientsRoute || isAnalysisRoute;
  const hideShellTopbar =
    isGridRoute || isHomeRoute || isLibraryRoute || isClientsRoute || isAnalysisRoute;

  useEffect(() => {
    if (email) identify(email, name ? { name } : undefined);
  }, [email, name, identify]);

  const handleSidebarToggle = (): void => {
    const nextDrawerState = !sidebarOpen;
    setSidebarOpen(nextDrawerState);
    localStorage.setItem('isSidebarHidden', String(!nextDrawerState));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {!hideShellTopbar && (
        <AppBar position="static" elevation={0} sx={{ flexShrink: 0, height: 52, display: 'flex', flexDirection: 'row', alignItems: 'center', px: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {pageTitle}
          </Typography>
        </AppBar>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <ShellSidebar
          expanded={sidebarOpen}
          onToggle={handleSidebarToggle}
          navItems={navItems}
          isNavItemActive={(path) => isNavItemActive(location.pathname, path)}
          onNavigate={(path) => navigate(path)}
          clients={clients}
          selectedClientExternalUuid={selectedClientExternalUuid}
          selectedClientName={selectedClient?.name ?? null}
          onClientChange={setSelectedClientExternalUuid}
          name={name}
          email={email}
          isInternalUser={isInternalUser}
          isPoweruser={isPoweruser}
          avatarUrl={avatarUrl}
        />

        <Box
          component="main"
          sx={{
            '--ee-shell-sidebar-width': `${drawerWidth}px`,
            '--ee-shell-topbar-height': hideShellTopbar ? '0px' : '52px',
            flexGrow: 1,
            minHeight: 0,
            overflowY: isFullBleedRoute ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            p: hideShellTopbar ? 0 : 3,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Outlet />
        </Box>
        <HypeChatBox user={user}/>
      </Box>
    </Box>
  );
}
